/** Zod schemas for Projects, Sources, wiki pages, and context */

import { z } from 'zod'
import { HEALTH_DEFAULT_STALE_DAYS } from '../types/wiki-health'
import { WIKI_TEMPLATE_IDS } from '../types/wiki-template'

export const PROJECT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/
/** Max UTF-8 bytes of text content per source */
export const MAX_SOURCE_BYTES = 512 * 1024
/** Max characters of a wiki page body */
export const MAX_WIKI_PAGE_CHARS = 200_000
export const DEFAULT_CONTEXT_BUDGET = 4000
export const MAX_CONTEXT_BUDGET = 32_000

/** Fixed skeleton of the compiled wiki: software | research | personal | custom */
export const wikiTemplateSchema = z.enum(WIKI_TEMPLATE_IDS)

export const createProjectSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: z.string().trim().regex(PROJECT_SLUG_PATTERN, 'slug must be lowercase letters, digits, and dashes').optional(),
  description: z.string().trim().max(1000).optional(),
  repoUrl: z.string().trim().max(500).optional(),
  wikiTemplate: wikiTemplateSchema.optional(),
})

export const updateProjectSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(1000).nullable().optional(),
  repoUrl: z.string().trim().max(500).nullable().optional(),
  wikiTemplate: wikiTemplateSchema.optional(),
})

export const SOURCE_ORIGIN_PATTERN = /^[a-z0-9][a-z0-9._-]{0,39}$/

export const pushSourceSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  title: z.string().trim().min(1).max(255).optional(),
  content: z.string().min(1),
  contentType: z.string().trim().max(100).default('text/markdown'),
  origin: z.string().trim().toLowerCase().regex(SOURCE_ORIGIN_PATTERN, 'origin must be a short slug such as claude-code, codex, cli').default('api'),
  originRef: z.string().trim().max(500).optional(),
})

export const listSourcesSchema = z.object({
  status: z.enum(['active', 'superseded', 'archived', 'all']).default('active'),
  search: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})

export const WIKI_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?$/

export const upsertWikiPageSchema = z.object({
  title: z.string().trim().min(1).max(200),
  content: z.string().min(1).max(MAX_WIKI_PAGE_CHARS),
  topic: z.string().trim().max(100).optional(),
  summary: z.string().trim().max(1000).optional(),
  changeSummary: z.string().trim().max(500).optional(),
  /** optimistic concurrency: current version the writer based its edit on */
  expectedVersion: z.number().int().min(1).optional(),
  /** always wait in the review queue, even where this writer's edits are normally added at once */
  review: z.boolean().optional(),
})

export const listWikiProposalsSchema = z.object({
  status: z.enum(['pending', 'applied', 'discarded', 'all']).default('pending'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

export const decideWikiProposalSchema = z.object({
  note: z.string().trim().max(500).optional(),
})

/** Pin or unpin one section; toggles avoid lost updates when two people edit pins at once */
export const setWikiPinSchema = z.object({
  anchor: z.string().trim().min(1).max(80),
  pinned: z.boolean(),
})

export const contextRequestSchema = z.object({
  task: z.string().trim().min(1).max(2000),
  budget: z.number().int().min(200).max(MAX_CONTEXT_BUDGET).default(DEFAULT_CONTEXT_BUDGET),
  includeSources: z.boolean().default(true),
  scope: z.enum(['project', 'workspace']).default('project'),
})

export const healthQuerySchema = z.object({
  type: z.enum(['superseded_source', 'contradiction', 'stale_page', 'search_gap']).optional(),
  staleDays: z.coerce.number().int().min(1).max(365).default(HEALTH_DEFAULT_STALE_DAYS),
  includeDismissed: z.preprocess((v) => v === true || v === 'true' || v === '1', z.boolean()).default(false),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(10_000).default(0),
})

/** A finding key, as returned by the health report */
export const healthFindingKeySchema = z.object({
  key: z.string().trim().min(1).max(300),
})

/** Client-chosen id that makes a retried question return the first result instead of charging again */
export const ASK_REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/

/** Tokens of retrieved context an Ask may use: the web offers 2k/4k/8k; omitted keeps the server default */
export const ASK_BUDGET_MIN = 1000
export const ASK_BUDGET_MAX = 16_000

export const askRequestSchema = z.object({
  question: z.string().trim().min(3).max(1000),
  budget: z.number().int().min(ASK_BUDGET_MIN).max(ASK_BUDGET_MAX).optional(),
  requestId: z.string().regex(ASK_REQUEST_ID_PATTERN, 'requestId must be 8-64 letters, digits, dashes, or underscores').optional(),
})

/** Parse a token budget such as "4000", "4k", or "1.5k" */
export function parseTokenBudget(input: string | number | undefined): number | null {
  if (input === undefined) return DEFAULT_CONTEXT_BUDGET
  if (typeof input === 'number') return Number.isInteger(input) && input > 0 ? input : null
  const match = /^\s*(\d+(?:\.\d+)?)\s*(k)?\s*$/i.exec(input)
  if (!match) return null
  const value = Math.round(Number(match[1]) * (match[2] ? 1000 : 1))
  return value > 0 ? value : null
}

export type CreateProjectInput = z.infer<typeof createProjectSchema>
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>
export type PushSourceInput = z.input<typeof pushSourceSchema>
export type ListSourcesInput = z.input<typeof listSourcesSchema>
export type UpsertWikiPageInput = z.infer<typeof upsertWikiPageSchema>
export type ContextRequestInput = z.input<typeof contextRequestSchema>
export type HealthQueryInput = z.input<typeof healthQuerySchema>
export type AskRequestInput = z.input<typeof askRequestSchema>
